import { AnimatePresence, motion } from 'framer-motion'
import { useEffect } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import SVG from 'react-inlinesvg'
import { useNavigate } from 'react-router'

import Button from '@/components/Button'
import Loader from '@/components/Loader'
import View from '@/layout/View'
import { useCreateOrderMutation } from '@/lib/mutations'

import { CheckoutBody, CheckoutFooter, CheckoutLogo, CheckoutWrapper } from './Checkout.styles'
import Cart from './components/Cart'
import Confirmation from './components/Confirmation'
import PaymentMethod from './components/PaymentMethod'
import PaymentPending from './components/PaymentPending'
import SummaryBar from './components/SummaryBar/SummaryBar'
import useCheckout from './hooks/useCheckout'

const Checkout = () => {
  const { cart, currentStep, setCurrentStep, toOrderItems, setOrderId, setPaymentError } = useCheckout()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { mutateAsync: createOrder, isPending: isCreateOrderPending } = useCreateOrderMutation({
    onError: () => {
      toast.error(t('paymentError'))
    }
  })

  const handleSubmit = async () => {
    if (currentStep === 'CART') {
      setCurrentStep('PAYMENT_METHOD')
      return
    }

    if (currentStep === 'PAYMENT_METHOD') {
      if (process.env.REACT_APP_SKIP_TERMINAL === 'true') {
        setPaymentError(undefined)
        setCurrentStep('CONFIRMATION')
        return
      }
      setPaymentError(undefined)
      try {
        const data = await createOrder({
          attractionsOrder: {
            items: toOrderItems()
          }
        })
        setOrderId(data.id)
      } catch (error) {
        setPaymentError(error instanceof Error ? error.message : 'An unknown error occurred')
        return
      }

      setCurrentStep('PAYMENT_PENDING')
    }
  }

  useEffect(() => {
    if (cart.length === 0) {
      navigate('/tickets')
    }
  }, [cart])

  return (
    <View showOverlay={isCreateOrderPending}>
      <CheckoutWrapper>
        <CheckoutLogo>
          <SVG src={require('@/assets/img/logo.svg').default}></SVG>
        </CheckoutLogo>
        <CheckoutBody>
          <AnimatePresence mode="wait">
            {currentStep === 'CART' && <Cart key="CART" />}
            {currentStep === 'PAYMENT_METHOD' && <PaymentMethod key="PAYMENT_METHOD" />}
            {currentStep === 'PAYMENT_PENDING' && <PaymentPending key="PAYMENT_PENDING" />}
            {currentStep === 'CONFIRMATION' && <Confirmation key="CONFIRMATION" />}
          </AnimatePresence>
        </CheckoutBody>
        <AnimatePresence>
          {(currentStep === 'CART' || currentStep === 'PAYMENT_METHOD') && (
            <CheckoutFooter as={motion.div} initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} transition={{ duration: 0.2 }}>
              <SummaryBar
                actionBack={
                  <Button
                    variant="secondary"
                    onClick={() => {
                      if (currentStep === 'CART') {
                        navigate('/tickets')
                        return
                      }

                      setCurrentStep('CART')
                    }}
                  >
                    {t('back')}
                  </Button>
                }
                actionSubmit={
                  <Button disabled={cart.length === 0 || isCreateOrderPending} onClick={handleSubmit}>
                    {currentStep === 'CART' ? t('goNext') : t('pay')}
                  </Button>
                }
              />
            </CheckoutFooter>
          )}
          {isCreateOrderPending && <Loader cover />}
        </AnimatePresence>
      </CheckoutWrapper>
    </View>
  )
}

export default Checkout
